import React, { useEffect, useMemo, useState } from 'react';

import { Box, Center, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { WorkoutDetailView, WorkoutTimerButton } from 'components/Workout';
import {
  STICKY_NAV_OFFSET,
  useIsLargeDisplay,
  WHITEBOARD_SIZES,
} from 'components/Workout/whiteboardDisplay';
import { useImmersiveMode } from 'context/immersive-mode-context';
import dayjs from 'dayjs';
import useWorkoutDetailsByType from 'hooks/workout/useWorkoutDetailsByType';
import useWorkoutsCalendar from 'hooks/workout/useWorkoutsCalendar';
import { useLocation } from 'react-router-dom';
import type { WorkoutCalendarKiloClassType } from 'types/generated/models/WorkoutCalendarKiloClassType';

import WhiteboardClassNav, { type WhiteboardTab } from './components/WhiteboardClassNav';
import WhiteboardResultsTab from './components/WhiteboardResultsTab';

interface LocationState {
  kiloClassTypeId?: string;
  date?: string;
  kiloClassId?: string;
}

const Whiteboard = (): JSX.Element => {
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const isDesktop = useIsLargeDisplay();

  const [selectedDate, setSelectedDate] = useState<Date>(
    locationState?.date ? dayjs(locationState.date).toDate() : new Date(),
  );
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WhiteboardTab>('whiteboard');

  const { isActive: isFullscreen, toggle: toggleFullscreen, exit: exitImmersive } = useImmersiveMode();

  useEffect(() => () => exitImmersive(), [exitImmersive]);

  const dateString = dayjs(selectedDate).format('YYYY-MM-DD');

  const { data: calendarData, isLoading: isTypesLoading } = useWorkoutsCalendar({
    startDate: dateString,
    endDate: dateString,
  });

  const classTypes: WorkoutCalendarKiloClassType[] = useMemo(
    () => calendarData?.data?.[0]?.kiloClassTypes ?? [],
    [calendarData],
  );

  useEffect(() => {
    if (classTypes.length === 0) return;
    const currentIsValid = classTypes.some(t => t.kiloClassTypeId === selectedTypeId);
    if (currentIsValid) return;
    const requested = locationState?.kiloClassTypeId;
    const requestedIsValid = !!requested && classTypes.some(t => t.kiloClassTypeId === requested);
    setSelectedTypeId(requestedIsValid ? requested : classTypes[0].kiloClassTypeId);
  }, [classTypes, selectedTypeId, locationState?.kiloClassTypeId]);

  const { data: workoutData, isLoading: isWorkoutLoading } = useWorkoutDetailsByType({
    kiloClassTypeId: selectedTypeId ?? undefined,
    date: dateString,
  });

  const workout = workoutData?.data ?? null;

  const handleDateChange = (date: Date): void => {
    setSelectedDate(date);
    setSelectedTypeId(null);
  };

  const renderBody = (): JSX.Element => {
    if (isWorkoutLoading) {
      return (
        <Center py='xl'>
          <Loader />
        </Center>
      );
    }
    if (activeTab === 'results') {
      return (
        <WhiteboardResultsTab
          selectedTypeId={selectedTypeId}
          date={dateString}
          workout={workout}
          initialClassId={locationState?.kiloClassId}
        />
      );
    }
    if (!selectedTypeId) {
      return (
        <Center py='xl'>
          <Text color='dimmed'>Select a class to view the workout.</Text>
        </Center>
      );
    }
    if (!workout) {
      return (
        <Center py='xl'>
          <Text color='dimmed'>No workout assigned to this class.</Text>
        </Center>
      );
    }
    return (
      <Box
        sx={{
          maxWidth: isDesktop
            ? WHITEBOARD_SIZES.contentMaxWidth.large
            : WHITEBOARD_SIZES.contentMaxWidth.compact,
          width: '100%',
          marginInline: 'auto',
        }}
      >
        <Stack spacing='lg'>
          <Title order={1} size={isDesktop ? WHITEBOARD_SIZES.workoutTitle : undefined}>
            {workout?.name ?? ''}
          </Title>
          <WorkoutDetailView
            workout={workout}
            isCoachView
            isFullscreen={isFullscreen}
            enlarged={isDesktop}
          />
        </Stack>
      </Box>
    );
  };

  return (
    // Vertical-only, via native overflow rather than Mantine's ScrollArea: Radix's
    // ScrollArea.Viewport sets overflowX/overflowY as an inline style driven by which
    // Scrollbar sub-components are mounted, and Mantine's wrapper always mounts both —
    // so overflowX can never be forced to 'hidden' through its styles/classNames API.
    // The page itself must never scroll sideways, so any content wider than the viewport
    // (e.g. a workout with many movements) is forced to scroll within its own local
    // ScrollArea instead — otherwise this outer container picks up that overflow too,
    // dragging fixed/sticky page chrome like the Leaderboard toggle off-screen with it.
    <Box sx={{ height: '100vh', overflowY: 'auto', overflowX: 'hidden', backgroundColor: 'white' }}>
      <Box
        sx={t => ({
          position: 'fixed',
          top: STICKY_NAV_OFFSET,
          right: t.spacing.xl,
          zIndex: 300,
        })}
      >
        <WorkoutTimerButton />
      </Box>

      <Box
        sx={t => ({
          position: 'sticky',
          top: 0,
          zIndex: 200,
          backgroundColor: t.colors.gray[0],
          borderBottom: `1px solid ${t.colors.gray[3]}`,
        })}
      >
        <Container fluid px='xl' py='lg'>
          <WhiteboardClassNav
            classTypes={classTypes}
            selectedTypeId={selectedTypeId}
            onTypeSelect={setSelectedTypeId}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            isLoading={isTypesLoading}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
            showFullscreenButton={isDesktop}
          />
        </Container>
      </Box>

      <Container fluid px='xl' py='lg'>
        {activeTab === 'results' ? (
          <Stack spacing='lg'>
            <Title order={1} size={isDesktop ? WHITEBOARD_SIZES.workoutTitle : undefined}>
              {workout?.name ?? ''}
            </Title>
            {renderBody()}
          </Stack>
        ) : (
          renderBody()
        )}
      </Container>
    </Box>
  );
};

export default Whiteboard;
